import { supabase } from '@/lib/supabase';
import { filterByIdOrShortId } from '@/lib/shortId';
import { SUPABASE_URL } from '@/config';
import { paginationRange, buildPaginatedResponse, throwIfError } from '@/lib/supabase-helpers';
import { SeoAudit, SeoAuditIssue, SeoAuditPage } from '@/types/api/seo';

function mapAudit(row: any): SeoAudit {
  return {
    id: row.id,
    shortId: row.short_id ?? row.id,
    projectId: row.project_id,
    status: row.status,
    healthScore: row.health_score,
    pagesCrawled: row.pages_crawled ?? 0,
    issuesFound: row.issues_found ?? 0,
    crawlDepth: row.crawl_depth ?? 3,
    startedAt: row.started_at,
    completedAt: row.completed_at,
    errorMessage: row.error_message,
    createdBy: row.created_by,
    createdAt: row.created_at,
  };
}

function mapIssue(row: any): SeoAuditIssue {
  return {
    id: row.id,
    auditId: row.audit_id,
    pageId: row.page_id,
    category: row.category,
    severity: row.severity,
    issueType: row.issue_type,
    description: row.description,
    affectedUrl: row.affected_url,
    recommendation: row.recommendation,
    isResolved: row.is_resolved ?? false,
    resolvedAt: row.resolved_at,
    resolvedBy: row.resolved_by,
    createdAt: row.created_at,
  };
}

function mapPage(row: any): SeoAuditPage {
  return {
    id: row.id,
    auditId: row.audit_id,
    url: row.url,
    statusCode: row.status_code,
    title: row.title,
    metaDescription: row.meta_description,
    wordCount: row.word_count,
    loadTimeMs: row.load_time_ms,
    h1Count: row.h1_count,
    internalLinks: row.internal_links,
    externalLinks: row.external_links,
    imagesTotal: row.images_total,
    imagesNoAlt: row.images_no_alt,
    createdAt: row.created_at,
  };
}

const seoAuditsService = {
  async getAll({
    projectId,
    page,
    take,
  }: {
    projectId: string;
    page: number;
    take: number;
  }) {
    const [from, to] = paginationRange(page, take);
    const { data, error, count } = await supabase
      .from('seo_audits')
      .select('*', { count: 'exact' })
      .eq('project_id', projectId)
      .order('created_at', { ascending: false })
      .range(from, to);

    if (error) throw error;
    return buildPaginatedResponse((data ?? []).map(mapAudit), count ?? 0, page, take);
  },

  async getById(id: string): Promise<SeoAudit> {
    let query = supabase
      .from('seo_audits')
      .select('*');
    query = filterByIdOrShortId(query, id);
    const { data, error } = await query.single();

    if (error) throw error;
    return mapAudit(data);
  },

  async create(payload: {
    projectId: string;
    crawlDepth: number;
    createdBy: string;
  }): Promise<SeoAudit> {
    const result = await supabase
      .from('seo_audits')
      .insert({
        project_id: payload.projectId,
        crawl_depth: payload.crawlDepth,
        status: 'queued',
        created_by: payload.createdBy,
      })
      .select()
      .single();

    return mapAudit(throwIfError(result));
  },

  async getIssues({
    auditId,
    page,
    take,
    severity,
    category,
  }: {
    auditId: string;
    page: number;
    take: number;
    severity?: string;
    category?: string;
  }) {
    const [from, to] = paginationRange(page, take);
    let query = supabase
      .from('seo_audit_issues')
      .select('*', { count: 'exact' })
      .eq('audit_id', auditId)
      .order('severity', { ascending: true })
      .order('created_at', { ascending: false });

    if (severity) query = query.eq('severity', severity);
    if (category) query = query.eq('category', category);

    const { data, error, count } = await query.range(from, to);
    if (error) throw error;
    return buildPaginatedResponse((data ?? []).map(mapIssue), count ?? 0, page, take);
  },

  async getPages({
    auditId,
    page,
    take,
  }: {
    auditId: string;
    page: number;
    take: number;
  }) {
    const [from, to] = paginationRange(page, take);
    const { data, error, count } = await supabase
      .from('seo_audit_pages')
      .select('*', { count: 'exact' })
      .eq('audit_id', auditId)
      .order('url', { ascending: true })
      .range(from, to);

    if (error) throw error;
    return buildPaginatedResponse((data ?? []).map(mapPage), count ?? 0, page, take);
  },

  async resolveIssue(issueId: string, resolvedBy: string): Promise<SeoAuditIssue> {
    const result = await supabase
      .from('seo_audit_issues')
      .update({
        is_resolved: true,
        resolved_at: new Date().toISOString(),
        resolved_by: resolvedBy,
      })
      .eq('id', issueId)
      .select()
      .single();

    return mapIssue(throwIfError(result));
  },

  async startCrawl(auditId: string, projectId: string): Promise<void> {
    const { data: { user } } = await supabase.auth.getUser();
    const { data: { session } } = await supabase.auth.getSession();

    // Fire-and-forget: don't await the full crawl response (can take 60s+).
    // The edge function updates the audit record directly — the detail page polls for status.
    const supabaseUrl = SUPABASE_URL;
    fetch(`${supabaseUrl}/functions/v1/seo-audit-crawl`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'Authorization': `Bearer ${session?.access_token ?? ''}`,
      },
      body: JSON.stringify({ action: 'startAudit', auditId, projectId, userId: user?.id }),
    }).catch((err) => console.error('[SEOx] startCrawl fetch failed:', err));
  },

  async getAuditStatus(auditId: string): Promise<SeoAudit> {
    const { data: { user } } = await supabase.auth.getUser();
    const { data, error } = await supabase.functions.invoke('seo-audit-crawl', {
      body: { action: 'getAuditStatus', auditId, userId: user?.id },
    });
    if (error) throw error;
    return mapAudit(data.audit);
  },
};

export default seoAuditsService;
